import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { BookOpen, FolderOpen, FileText, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import useDocumentTitle from "@/hooks/useDocumentTitle";

function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let x = n;
  while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function TeachIndexPage() {
  useDocumentTitle("Teach", "Teaching materials library");
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/teaching/qualifications");
      setList(data || []);
    } catch (err) {
      if (!isAuthError(err)) toast.error("Failed to load qualifications");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Distinct categories present in the data, plus a couple of "always-shown"
  // buckets Jamie asked for so the picker feels stable even before quals are
  // tagged. Editable via Qualifications page.
  const categories = Array.from(new Set([
    "Domestic Gas", "Commercial Gas", "Renewables", "Electrical",
    "Domestic ACS", "Commercial ACS",
    ...list.map(q => q.category || "Uncategorised"),
  ])).filter(Boolean).sort();

  const filtered = list.filter(q => {
    if (category !== "all" && (q.category || "Uncategorised") !== category) return false;
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (q.name || "").toLowerCase().includes(f)
      || (q.code || "").toLowerCase().includes(f)
      || (q.category || "").toLowerCase().includes(f);
  });

  // Group by category
  const grouped = filtered.reduce((acc, q) => {
    const cat = q.category || "Uncategorised";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(q);
    return acc;
  }, {});
  const totalFiles = list.reduce((sum, q) => sum + (q.material_count || 0), 0);

  return (
    <div className="space-y-5" data-testid="teach-index">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-semibold flex items-center gap-2">
            <BookOpen size={18} /> Teaching materials
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            One folder per qualification. Upload PowerPoints, lesson plans, handouts and any other course documents — lecturers can download them when teaching.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[11px]">
            <FileText size={11} className="mr-1" /> {totalFiles} files
          </Badge>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9 text-sm px-2 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            data-testid="teach-category-filter"
          >
            <option value="all">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter qualifications…"
            className="h-9 text-sm px-3 rounded-md border border-slate-200 bg-white w-56 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            data-testid="teach-filter"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border bg-white p-10 text-center text-sm text-muted-foreground">
          No qualifications match your filter.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 font-medium">{cat}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map(q => (
                  <button
                    key={q.id}
                    onClick={() => nav(`/teach/${q.id}`)}
                    className="text-left rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-sm transition group"
                    data-testid={`teach-card-${q.code || q.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-slate-900 text-white px-2 py-0.5 rounded">
                        <FolderOpen size={10} /> {q.code || "Course"}
                      </span>
                      <ChevronRight size={16} className="text-slate-400 group-hover:text-slate-900" />
                    </div>
                    <div className="font-heading text-base font-semibold text-slate-900 line-clamp-2">{q.name}</div>
                    {q.description && (
                      <p className="text-xs text-slate-600 mt-2 line-clamp-2">{q.description}</p>
                    )}
                    <div className="text-xs text-muted-foreground mt-3 flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <FileText size={11} /> {q.material_count || 0} {q.material_count === 1 ? "file" : "files"}
                      </span>
                      {q.material_total_size > 0 && (
                        <span className="text-slate-400">{formatBytes(q.material_total_size)}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
